import {
  Drawer,
  Group,
  Stack,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { Button, Loader, Select } from "@tfg/ui"
import { sortBy } from "lodash"
import { useNavigate } from "react-router-dom"

import {
  organizationSchema,
  organizationValidationType,
} from "@tfg/types/schemas/admin"

import { LoginAs } from "./OrganizationDetails"
import {
  useCreateOrganizationMutation,
  useListOrganizationsQuery,
} from "./hooks"

export const OrganizationsAdmin = () => {
  const {
    data: organizations,
    isLoading,
    isError,
  } = useListOrganizationsQuery()
  const navigate = useNavigate()

  const [drawerOpen, drawerActions] = useDisclosure(false)

  const { mutate: createOrganization, isLoading: isCreating } =
    useCreateOrganizationMutation({
      onSuccess: drawerActions.close,
    })
  const form = useForm<organizationValidationType>({
    validate: zodResolver(organizationSchema),
    initialValues: {
      name: "",
      agecProductPagePlan: "STARTER",
      outputCustomizationPlan: "ESSENTIAL",
      lcaCustomizationPlan: "ADVANCED",
      customFieldsPlan: "ADVANCED",
      certificatesPlan: "ADVANCED",
      zalandoPlan: "DISABLED",
      rduePlan: "DISABLED",
      businessType: "SMALL_BUSINESS",
    },
  })
  const handleSubmit = (values: organizationValidationType) => {
    createOrganization(values)
  }
  if (isError) return <Text>Organizations could not be loaded</Text>

  return (
    <Stack>
      <Group justify="right">
        <Button onClick={drawerActions.open} icon={<IconPlus />}>
          Create Organization
        </Button>
      </Group>
      {isLoading ? (
        <Loader fullPage />
      ) : (
        <Table verticalSpacing="sm" highlightOnHover>
          <TableThead>
            <TableTr>
              <TableTh>Name</TableTh>
              <TableTh />
            </TableTr>
          </TableThead>
          <TableTbody>
            {sortBy(organizations, (organization) => organization.name).map(
              (organization) => (
                <TableTr
                  key={organization.id}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/admin/organizations/${organization.id}`)
                  }
                >
                  <TableTd>{organization.name}</TableTd>
                  <TableTd align="right">
                    {organization.users[0] && (
                      <Group gap="var(--tfg-molecule-padding-y)" w="100%">
                        <LoginAs
                          showEmail={true}
                          user={organization.users[0]}
                          withoutAdmin={true}
                          onLogin={() => window.location.replace("/")}
                        />
                      </Group>
                    )}
                  </TableTd>
                </TableTr>
              )
            )}
          </TableTbody>
        </Table>
      )}
      <Drawer
        position="right"
        opened={drawerOpen}
        onClose={() => drawerActions.close()}
        title={<Title order={2}>Create brand</Title>}
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Name"
              placeholder="Company name"
              {...form.getInputProps("name")}
            />
            <Select
              label="Agec Product Page Plan"
              data={[
                { label: "Starter", value: "STARTER" },
                { label: "Essential", value: "ESSENTIAL" },
                { label: "Advanced", value: "ADVANCED" },
              ]}
              {...form.getInputProps("agecProductPagePlan")}
            />
            <Select
              label="Output Customization Plan"
              data={[
                { label: "Essential", value: "ESSENTIAL" },
                { label: "Advanced", value: "ADVANCED" },
                { label: "Expert", value: "EXPERT" },
              ]}
              {...form.getInputProps("outputCustomizationPlan")}
            />
            <Select
              label="LCA"
              data={[
                { label: "Advanced", value: "ADVANCED" },
                { label: "Expert", value: "EXPERT" },
              ]}
              {...form.getInputProps("lcaCustomizationPlan")}
            />
            <Select
              label="Certificates"
              data={[
                { label: "Advanced", value: "ADVANCED" },
                { label: "Expert", value: "EXPERT" },
              ]}
              {...form.getInputProps("certificatesPlan")}
            />
            <Select
              label="Rdue Plan"
              data={[
                { label: "Yes", value: "ENABLED" },
                { label: "No", value: "DISABLED" },
              ]}
              {...form.getInputProps("rduePlan")}
            />
            <Select
              label="Zalando Plan"
              data={[
                { label: "Yes", value: "ENABLED" },
                { label: "No", value: "DISABLED" },
              ]}
              {...form.getInputProps("zalandoPlan")}
            />
            <Button loading={isCreating} type="submit">
              Create
            </Button>
          </Stack>
        </form>
      </Drawer>
    </Stack>
  )
}
